import AppCommandBar from 'app/AppCommandBar'
import { FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { StickyPane } from 'components/StickyPane'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { FormikActions } from 'formik'
import { MaintenanceForm, MaintenanceFormValues } from 'forms/MaintenanceForm'
import { omitDeep } from 'helpers'
import {
  useCreateCommand,
  useCustomSearchCommand,
  useDeleteCommand,
  useEditCommand,
} from 'hooks/useCommand'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { useRouteParams } from 'hooks/useRouteParams'
import { useSelection } from 'hooks/useSelection'
import { pick } from 'lodash'
import { MaintenanceArea, MaintenanceAreaInsertInput, UserRole } from 'model'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { FormType } from 'views/helpers'
import {
  useCreateMaintenanceAreaMutation,
  useDeleteMaintenanceAreaMutation,
  useReadMaintenanceAreasQuery,
  useUpdateMaintenanceAreaMutation,
} from './AdminMaintenanceView.generated'
import { MaintenanceList } from './MaintenanceList'

const AdminMaintenanceListView: React.FC = () => {
  const { t } = useTranslation()

  const [createMaintenanceArea] = useCreateMaintenanceAreaMutation()
  const [updateMaintenanceArea] = useUpdateMaintenanceAreaMutation()
  const [deleteMaintenanceAreas] = useDeleteMaintenanceAreaMutation()
  const [formType, setFormType] = React.useState()
  const [selectedItemId, setSelectedItemId] = React.useState()
  const [dialogState, showDialog, hideDialog] = useDialogState()
  const [panelState, showPanel, hidePanel] = useFormikPanelState()

  const [routeParams, updateRouteParams] = useRouteParams(
    'admin.data.maintenanceAreas',
    {
      search: undefined,
    }
  )

  const { loading, data, error, refetch } = useReadMaintenanceAreasQuery({
    variables: { search: routeParams.search },
  })

  const items =
    data && data.maintenanceAreas
      ? data.maintenanceAreas.filter((x) => !x._deleted)
      : []

  const [selection, selectedItems] = useSelection<MaintenanceArea>({
    getKey: (item) => item._id!,
  })

  useCustomSearchCommand(
    {
      value: routeParams.search,
      placeholder: t('model.maintenanceArea.search'),
      onSearch: (value) => updateRouteParams({ search: value }),
      onClear: () => updateRouteParams({ search: undefined }),
    },
    [t, routeParams, updateRouteParams]
  )

  useCreateCommand(
    'maintenanceArea',
    {
      priority: 10,
      onClick: () => {
        setFormType(FormType.insert)
        showPanel({ inactive: false })
      },
    },
    [setFormType, showPanel]
  )

  useEditCommand(
    { name: 'maintenanceArea', count: selectedItems.length },
    {
      priority: 20,
      onClick: () => {
        setFormType(FormType.update)
        setSelectedItemId(selectedItems[0]._id)
        showPanel(
          omitDeep(selectedItems[0], ['id', '__typename']) as MaintenanceArea
        )
      },
    },
    [selectedItems, setFormType]
  )

  useDeleteCommand(
    { name: 'maintenanceArea', count: selectedItems.length },
    {
      priority: 30,
      onClick: showDialog,
    },
    [selectedItems]
  )

  const handleSubmit = React.useCallback(
    async (
      values: MaintenanceFormValues,
      formikActions: FormikActions<MaintenanceFormValues>
    ) => {
      formikActions.setSubmitting(true)
      if (formType === FormType.insert) {
        await createMaintenanceArea({
          variables: { input: values as MaintenanceAreaInsertInput },
        }).then((_) =>
          refetch({
            variables: { search: routeParams.search },
          } as any)
        )
      } else if (formType === FormType.update) {
        await updateMaintenanceArea({
          variables: {
            id: selectedItemId,
            update: pick(values, [
              'department',
              'maintenanceArea',
              'maintenanceHead',
              'inactive',
              'annotation',
            ]),
          },
        }).then((_) =>
          refetch({
            variables: { search: routeParams.search },
          } as any)
        )
      } else {
        throw new Error('ERROR')
      }
      formikActions.setSubmitting(false)
      const selectedKeys = selectedItems.map(
        (maintenceArea) => maintenceArea._id
      )
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
      hidePanel()
    },
    [
      formType,
      routeParams.search,
      selectedItemId,
      selection,
      selectedItems,
      hidePanel,
      refetch,
      updateMaintenanceArea,
      createMaintenanceArea,
    ]
  )

  const handleConfirmDelete = React.useCallback(async () => {
    if (selectedItems.length > 0) {
      const ids = selectedItems.map((ft) => ft._id)
      await deleteMaintenanceAreas({
        variables: { ids },
      })
    }
    hideDialog()
    refetch()
  }, [selectedItems, deleteMaintenanceAreas, refetch, hideDialog])

  return (
    <FlexSpinner loading={loading} error={error}>
      <MaintenanceList items={items} selection={selection} />
      <FormikPanel
        {...panelState}
        onSubmit={handleSubmit}
        headerText={
          panelState.initialValues && panelState.initialValues._id
            ? t('field.maintenanceArea.edit.label')
            : t('field.maintenanceArea.create.label')
        }
      >
        <MaintenanceForm />
      </FormikPanel>
      <ConfirmDeleteDialog
        {...dialogState}
        onConfirm={handleConfirmDelete}
        itemCount={selectedItems.length}
      />
    </FlexSpinner>
  )
}

export const AdminMaintenanceView: React.FC = () => (
  <ViewAuthorizer
    roles={[UserRole.SystemAdministrator, UserRole.ResourceManager]}
  >
    <StickyPane>
      <AppCommandBar />
      <AdminMaintenanceListView />
    </StickyPane>
  </ViewAuthorizer>
)
